import type { SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import type { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { Table } from '../components/Table';
import { getAllTagsInAccount, isFetchTagsInProgressRtk } from '../../../../../Drivers.selectors';
import type { Driver, DriverStatus, Tag, User } from '../../../../../../types';
import type { Tenant } from '../../../../../../configuration/types';
import { selectDriver, sortDrivers } from '../../../../thunks/tableThunks';
import { deselectDriver } from '../../../../../crossSliceThunks';
import {
    driversListModeChanged,
    getDriversListModeRtk,
    getDriversRtk,
    getDriverStatusesRtk,
    getSearchTextRtk,
    getSelectedDriverIdRtk,
    getSortByRtk,
    getSortDirRtk,
    getUsersRtk,
    hasFetchedDriversOnceRtk,
    hasFetchDriversRequestedRtk,
    hasFetchErrorRtk,
    searchTextChanged,
    toggleDriverCreationDialog,
} from '../../../../appSlice';
import { getSelectedDriverRtk, isFetchDriverInProgressRtk } from '../../../sidebar/sidebarSlice';
import { getTenant, getUserAccount } from '../../../../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../../../../configuration/setup/hooks';
import { useGetDriversQuery } from '../../../../services/driversApi';
import { config } from '../../../../../../config';

export interface TablePropertiesFromState {
    drivers: Driver[];
    users: User[];
    driverStatuses: DriverStatus[];
    driversListMode: TableViewTogglesViewType;
    fetchDriversRequested: boolean;
    fetchDriverRequested: boolean;
    hasDataFetchError: boolean;
    hasFetchedDriversOnce: boolean;
    searchText: string;
    selectedDriver: Driver | null;
    selectedDriverId: string | null;
    sortBy: string;
    sortDir: SortDirectionType;
    accountId: string | undefined;
    allTagsInAccount: Tag[];
    fetchTagsRequested: boolean;
    tenant: Tenant | null;
}

export interface TablePropertiesFromDispatch {
    searchTextChanged: (searchText: string) => void;
    selectionChange: (nextSelectedDriverId: string | undefined) => void;
    sortChange: (sortBy: string, sortDir: SortDirectionType) => void;
    viewTypeChange: (mode: TableViewTogglesViewType) => void;
    toggleDriverCreationDialog: (show: boolean) => void;
}

export const DriverTableWrapper = () => {
    const dispatch = useAppDispatch();
    const canQueryDrivers = Boolean(config.backend.DRIVERADMIN_SERVICE);

    useGetDriversQuery(undefined, {
        skip: !canQueryDrivers,
    });

    const props: TablePropertiesFromState & TablePropertiesFromDispatch = {
        drivers: useAppSelector(getDriversRtk),
        users: useAppSelector(getUsersRtk),
        driverStatuses: useAppSelector(getDriverStatusesRtk),
        driversListMode: useAppSelector(getDriversListModeRtk),
        fetchDriversRequested: useAppSelector(hasFetchDriversRequestedRtk),
        fetchDriverRequested: useAppSelector(isFetchDriverInProgressRtk),
        fetchTagsRequested: useAppSelector(isFetchTagsInProgressRtk),
        hasDataFetchError: useAppSelector(hasFetchErrorRtk),
        hasFetchedDriversOnce: useAppSelector(hasFetchedDriversOnceRtk),
        searchText: useAppSelector(getSearchTextRtk),
        selectedDriver: useAppSelector(getSelectedDriverRtk),
        selectedDriverId: useAppSelector(getSelectedDriverIdRtk),
        sortBy: useAppSelector(getSortByRtk),
        sortDir: useAppSelector(getSortDirRtk),
        accountId: useAppSelector(getUserAccount),
        allTagsInAccount: useAppSelector(getAllTagsInAccount),
        tenant: useAppSelector(getTenant),
        searchTextChanged: (searchText: string) => dispatch(searchTextChanged(searchText)),
        selectionChange: (nextSelectedDriverId: string | undefined) =>
            dispatch(nextSelectedDriverId ? selectDriver(undefined, nextSelectedDriverId) : deselectDriver()),
        sortChange: (sortBy: string, sortDir: SortDirectionType) => dispatch(sortDrivers(sortBy, sortDir)),
        viewTypeChange: (mode: TableViewTogglesViewType) => dispatch(driversListModeChanged(mode)),
        toggleDriverCreationDialog: (show: boolean) => dispatch(toggleDriverCreationDialog(show)),
    };

    return <Table {...props} />;
};
